import { createEffect, type JSX, on, Show } from 'solid-js';
import { t } from '../i18n';
import type { EditTarget } from '../lib/agent-config-editor';
import { createPromptVarForm } from '../lib/prompt-var-form';
import { PROMPT_VAR_SCOPE_LABEL } from '../lib/prompt-var-scope';
import {
  type ApiPromptVar,
  PROMPT_VAR_SCOPES,
  type PromptVarAddress,
  type PromptVarScope,
} from '../lib/prompt-vars';
import { Field } from './Field';
import { Modal } from './Modal';
import { Select } from './Select';
import { Button, buttonClass } from './ui/Button';

// Set / edit one prompt var. Shell + fields (small enough to stay one file) —
// `createPromptVarForm` holds the state and the route owns the PUT.
//
// The scope vocabulary comes off the DOMAIN enum the API parses `:scope` with,
// and the ref field appears exactly when that scope requires one — the API's own
// pairing rule, read from the same helper, never a second copy.
//
// The value box holds JSON because the wire field is JSON. A bare word is not,
// and quoting it silently would store `"true"` where the MCP verb stores `true`.
// The credential-shape refusal stays server-side: one detector, both doors, its
// 422 rendered inline.
//
// Always MOUNTED, driven by `target`: unmounting on close would cut the
// dialog's exit animation, so the fields re-seed from the target instead.

export function PromptVarFormModal(props: {
  /** The editor's target: a row to edit, `'new'` for a create, null when closed. */
  readonly target: EditTarget<ApiPromptVar>;
  readonly pending: boolean;
  /** The API's refusal, shown inline. */
  readonly error: string | null;
  readonly onSave: (at: PromptVarAddress, value: unknown) => void;
  readonly onClose: () => void;
}): JSX.Element {
  const form = createPromptVarForm(t('account_prompt_vars.invalid_json'));
  const promptVar = (): ApiPromptVar | null =>
    props.target === null || props.target === 'new' ? null : props.target;

  // Seeding local field state from an incoming prop is a legitimate effect: it
  // initializes on open, it does not mirror derived state.
  createEffect(
    on(
      () => props.target,
      () => form.seed(promptVar()),
    ),
  );

  const submit = (e: Event): void => {
    e.preventDefault();
    if (!form.valid() || props.pending) return;
    props.onSave(form.address(), form.parsedValue());
  };

  return (
    <Modal
      open={props.target !== null}
      size="lg"
      title={t(
        promptVar() === null ? 'account_prompt_vars.add_title' : 'account_prompt_vars.edit_title',
      )}
      desc={t('account_prompt_vars.form_desc')}
      onClose={props.onClose}
    >
      <form onSubmit={submit}>
        <div class="field-group">
          <Field
            label={t('account_prompt_vars.form_scope')}
            id="prompt-var-scope"
            desc={t('account_prompt_vars.scope_desc')}
          >
            {(a11y) => (
              <Select<PromptVarScope>
                a11y={a11y}
                label={t('account_prompt_vars.form_scope')}
                block
                value={form.scope()}
                /* The VALUE is the wire enum, the label is translated. */
                options={PROMPT_VAR_SCOPES.map((scope) => ({
                  value: scope,
                  label: t(PROMPT_VAR_SCOPE_LABEL[scope]),
                }))}
                onChange={form.setScope}
              />
            )}
          </Field>

          {/* Rendered only for the scopes that take one — account scope refuses a
              ref (400), so offering the box there would invite a refusal. */}
          <Show when={form.needsRef()}>
            <Field
              label={t('account_prompt_vars.form_ref')}
              id="prompt-var-ref"
              desc={t('account_prompt_vars.ref_desc')}
            >
              {(a11y) => (
                <input
                  {...a11y}
                  class="input"
                  type="text"
                  value={form.ref()}
                  onInput={(e) => form.setRef(e.currentTarget.value)}
                  required
                />
              )}
            </Field>
          </Show>

          <Field
            label={t('account_prompt_vars.form_key')}
            id="prompt-var-key"
            desc={t('account_prompt_vars.key_desc')}
          >
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                type="text"
                value={form.key()}
                onInput={(e) => form.setKey(e.currentTarget.value)}
                required
              />
            )}
          </Field>

          <Field
            label={t('account_prompt_vars.form_value')}
            id="prompt-var-value"
            desc={t('account_prompt_vars.value_desc')}
            error={form.valueError()}
          >
            {(a11y) => (
              <textarea
                {...a11y}
                class="input"
                rows="5"
                value={form.value()}
                onInput={(e) => form.setValue(e.currentTarget.value)}
                required
              />
            )}
          </Field>

          <Show when={props.error}>
            {(msg) => (
              <p class="field__error" role="alert">
                {msg()}
              </p>
            )}
          </Show>
        </div>

        <div class="dialog__footer">
          <Button variant="outline" onClick={props.onClose}>
            {t('account_prompt_vars.cancel')}
          </Button>
          <button type="submit" class={buttonClass()} disabled={!form.valid() || props.pending}>
            {t(props.pending ? 'account_prompt_vars.saving' : 'account_prompt_vars.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
